import { nanoid } from 'nanoid'
import { useEffect, useState } from 'react'
import { getIntegrationsFromBot } from '~/features/bots/services'
import { webchatIntegrationConfigSchema, type WebchatIntegrationConfig } from '~/features/webchat/schemas'
import { useUploadFile } from '~/hooks'
import { useUpdateBot } from '~/hooks/bots'
import { showSuccessToast, useSuspenseQuery } from '~/services'
import { useWebchatStore } from '~/stores'
import { isBase64 } from '~/utils'
import { Base64 } from 'js-base64'

export const useWebchatConfig = ({ botId, workspaceId }: { botId: string; workspaceId: string }) => {
  const bot = useSuspenseQuery('workspaces_/$workspaceId_/bots_/$botId_', { botId, workspaceId }).data
  const webchatIntegration = getIntegrationsFromBot(bot).find((bi) => bi.name === 'webchat')
  const baseConfig = webchatIntegrationConfigSchema.safeParse(webchatIntegration?.configuration ?? {})
  const [config, setConfig] = useState<WebchatIntegrationConfig>(
    baseConfig.success ? baseConfig.data : { frontendVersion: 'v1', theme: 'eggplant' }
  )
  const { mutate: updateBot, isPending, mutateAsync: updateBotAsync } = useUpdateBot()
  const setDirty = useWebchatStore((state) => state.setDirty)
  const { mutateAsync: uploadFile } = useUploadFile({ botId, workspaceId })

  useEffect(() => {
    setConfig(baseConfig.success ? baseConfig.data : { frontendVersion: 'v1', theme: 'eggplant' })
  }, [bot])

  const updateWebchatIntegration = (
    configuration: Record<string, any>,
    options: { showToast?: boolean } = { showToast: true }
  ) => {
    if (!webchatIntegration) {
      return
    }

    updateBot({
      id: botId,
      workspaceId,
      integrations: {
        [webchatIntegration.id]: { enabled: webchatIntegration.enabled, configuration },
      },

      onSuccess: () => {
        if (options.showToast) {
          showSuccessToast('The integration configuration has been updated.')
          setDirty(false)
        }
      },
    })
  }

  const updateWebchatIntegrationAsync = async (
    configuration: Record<string, any>,
    options: { showToast?: boolean } = { showToast: true }
  ) => {
    if (!webchatIntegration) {
      return
    }

    // If avatars are base64, we need to upload them to S3. This fixes a bug we had where the base64 was too long for the URL
    if (configuration.avatarUrl && isBase64(configuration.avatarUrl)) {
      const [type, content] = configuration.avatarUrl.split(';base64,')
      const avatar = Base64.toUint8Array(content)

      const file = await uploadFile({
        key: `bot-${botId}-avatar-${nanoid(6)}`,
        content: avatar,
        accessPolicies: ['public_content'],
        contentType: type.replace('data:', ''),
        publicContentImmediatelyAccessible: true,
        tags: { source: 'integration', integration: 'webchat' },
      })

      if (file) {
        configuration.avatarUrl = file.file.url
      }
    }

    if (configuration.botAvatarUrl && isBase64(configuration.botAvatarUrl)) {
      const [type, content] = configuration.botAvatarUrl.split(';base64,')
      const avatar = Base64.toUint8Array(content)

      const file = await uploadFile({
        key: `bot-${botId}-avatar-${nanoid(6)}`,
        content: avatar,
        accessPolicies: ['public_content'],
        contentType: type.replace('data:', ''),
        publicContentImmediatelyAccessible: true,
      })
      if (file) {
        configuration.botAvatarUrl = file.file.url
      }
    }

    await updateBotAsync({
      id: botId,
      workspaceId,
      integrations: {
        [webchatIntegration.id]: { enabled: webchatIntegration.enabled, configuration },
      },
    })
    if (options.showToast) {
      showSuccessToast('The integration configuration has been updated.')
      setDirty(false)
    }
  }

  return { config, setConfig, updateWebchatIntegration, updateWebchatIntegrationAsync, isPending, webchatIntegration }
}
